import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type PeriodDashboardView } from '@vello/api-client';
import { formatMoney } from '@vello/format';
import { ColumnChart, CountUp, DateRangeInput, DonutChart, EmptyState, ListTable, Notice, Panel, RingStat, SectionTitle, SegmentedControl, Sparkline, StatCard, type Column, Text, Row, Grid, Stack, useAutoRefresh } from '@vello/web-theme';
import {
  PERIOD_PRESETS,
  customPeriod,
  dayTrend,
  formatNowDelta,
  hourOfDayBars,
  marketDate,
  marketTimeZone,
  periodCards,
  periodDelta,
  presetDays,
  revenueRows,
  scopeTimeZone,
  statusSlices,
  type PeriodCard,
  type PeriodPreset,
  type RevenueRowView,
} from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/**
 * Visão geral › Período (W9-OPS-LIVE-MAP, D471, ADR-0089; DevMobility C-031, A-006; X-ray A-10): the rides requested in a
 * window and what became of them (no driver, completed, cancelled), the supply online now (market scope), the money PER
 * CURRENCY (what riders owed, what was received, the platform's commission, what stayed with drivers — exact, from integer
 * minor units) and a bar per local day. The window is whole local days of the market's zone: the from/to dates are always on
 * screen, and the quick presets (today, 7 days, 30 days) fill them — editing a date leaves the presets unpressed (owner,
 * 2026-10-12: no "custom" step, no auto-refresh switch). The numbers always refresh on their own every `refreshSeconds`
 * while the tab is visible. A rate over zero rides reads "unavailable", never 0 %.
 *
 * D513 (V-OPS-CHARTS): the KPI numbers count up (and tween old → new on an automatic refresh, never restart), the per-day
 * series is a stacked `ColumnChart` over EVERY local day of the window (a day with no ride is a real zero), the rides by what
 * became of them are a `DonutChart`, and the completion / driver-assigned rates are `RingStat`s. Money stays a table: it is
 * per currency and cannot be summed. Only what the endpoint already returns is drawn — no take rate, net revenue, ETA or
 * supply/demand (D488 fills those; they stay the honest "unavailable" KPIs of the screen above).
 */
const CARD_IDS = ['requested', 'noDriverRate', 'completed', 'completionRate', 'cancelled'] as const;

export function PeriodDashboard(): ReactElement {
  const { runtime, scope, markets } = useAdmin();
  const { t, lang } = useT();
  const { fail, refusal: refusalOf } = useVocab();
  const market = scope.current.city;
  // F3 (ADR-0124): a STATE is asked as itself (`region`, the markets that carry it) - never as its whole country. The window's days are the state's own zone
  // when its markets share one (else the browser's, and each market's hours stay on its own clock server-side).
  const region = market === undefined ? scope.current.region : undefined;
  const country = region === undefined ? scope.current.country : undefined;
  const timeZone = region !== undefined ? scopeTimeZone(scope.current, markets) : marketTimeZone(market, markets);

  // A preset follows "today" (a tab left open past local midnight moves with it); hand-edited dates stay put.
  const [preset, setPreset] = useState<PeriodPreset | undefined>('today');
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');
  const [view, setView] = useState<PeriodDashboardView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const seq = useRef(0);

  // Read at each load, so a preset left open past local midnight moves "today" on its next refresh.
  const daysNow = useCallback(() => (preset !== undefined ? presetDays(preset, new Date(), timeZone) : { fromDay, toDay }), [preset, fromDay, toDay, timeZone]);
  const periodNow = useCallback(() => {
    const d = daysNow();
    return customPeriod(d.fromDay, d.toDay, timeZone);
  }, [daysNow, timeZone]);
  const shown = daysNow();
  const valid = periodNow() !== undefined;
  /** Editing one date pins the window to what is on screen and unpresses the preset. */
  const editDay = (edge: 'from' | 'to', day: string): void => {
    setFromDay(edge === 'from' ? day : shown.fromDay);
    setToDay(edge === 'to' ? day : shown.toDay);
    setPreset(undefined);
  };

  const load = useCallback(async (): Promise<void> => {
    const window_ = periodNow();
    if (window_ === undefined) return;
    const mine = ++seq.current;
    const res = await runtime.ops.periodDashboard({ from: window_.from, to: window_.to, ...(market !== undefined ? { market } : region !== undefined ? { region } : country !== undefined ? { country } : {}) });
    if (mine !== seq.current) return;
    if (res.ok) {
      setView(res.value);
      setError(undefined);
    } else {
      setError(refusalOf('refusal.admin.live_map', res.error) ?? t('lmap.err.generic', { code: fail(res.error) }));
    }
  }, [runtime, periodNow, market, region, country, t]);

  useEffect(() => {
    setView(undefined);
    void load();
  }, [load]);

  const refreshMs = Math.max(10, view?.refreshSeconds ?? 60) * 1000;
  useAutoRefresh(load, { intervalMs: refreshMs, enabled: valid });

  const cards = periodCards(view);
  const rows = revenueRows(view);
  const trend = dayTrend(view, timeZone);
  const slices = statusSlices(view);
  const hours = hourOfDayBars(view);
  const nf = new Intl.NumberFormat(lang);
  const pf = new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 1 });
  const cardValue = (c: PeriodCard): string => (c.kind === 'unavailable' ? t('ui.unavailable') : c.kind === 'ratio' ? pf.format(c.value) : nf.format(c.value));
  const today = marketDate(new Date(), timeZone);
  const dayFormat = new Intl.DateTimeFormat(lang, { day: '2-digit', month: 'short', timeZone: 'UTC' });
  const dayLabel = (day: string): string => (day === today ? t('lper.preset.today') : dayFormat.format(new Date(`${day}T12:00:00.000Z`)));
  const loadingCharts = view === undefined && error === undefined;
  /** The little trend beside a count card: the same per-day series the column chart draws, only when there are two days to compare. */
  const sparkFor = (id: string): ReactElement | undefined => {
    const values = id === 'requested' ? trend.map((d) => d.requested) : id === 'completed' ? trend.map((d) => d.completed) : undefined;
    if (values === undefined || values.length < 2) return undefined;
    return (
      <span className="vello-admin-a-spark">
        <Sparkline values={values} labels={trend.map((d) => dayLabel(d.day))} title={t(`lper.kpi.${id}`)} height={32} formatValue={(n) => nf.format(n)} />
      </span>
    );
  };
  /** D559: the change of the two headline counts against the window just before the period (absent until the server returns it). */
  const deltaProp = (id: string): { delta?: { text: string; direction: 'up' | 'down' | 'flat'; tone: 'success' | 'danger' | 'neutral'; srLabel: string } } => {
    const d = id === 'requested' || id === 'completed' ? periodDelta(id, view) : undefined;
    return d === undefined ? {} : { delta: { text: t('lper.delta.vs', { value: formatNowDelta(d, lang) }), direction: d.direction, tone: d.tone, srLabel: t('lper.delta.sr') } };
  };
  const money = (amountMinor: bigint, currency: string): string => formatMoney({ amountMinor, currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' });

  const columns: readonly Column<RevenueRowView>[] = [
    { key: 'currency', header: t('lper.col.currency'), width: '0.6fr', render: (r) => <strong>{r.currency}</strong> },
    { key: 'rides', header: t('lper.col.rides'), width: '0.6fr', render: (r) => <span className="tnum">{new Intl.NumberFormat(lang).format(r.rides)}</span> },
    { key: 'due', header: t('lper.col.due'), width: '1fr', render: (r) => <span className="tnum">{money(r.dueMinor, r.currency)}</span> },
    { key: 'received', header: t('lper.col.received'), width: '1fr', render: (r) => <span className="tnum">{money(r.receivedMinor, r.currency)}</span> },
    { key: 'platform', header: t('lper.col.platform'), width: '1fr', render: (r) => <span className="tnum">{money(r.platformMinor, r.currency)}</span> },
    { key: 'drivers', header: t('lper.col.drivers'), width: '1fr', render: (r) => <span className="tnum">{money(r.driversMinor, r.currency)}</span> },
  ];

  return (
    <Stack as="section" gap={5} aria-labelledby="lper-title">
      <Text as="h2" role="title" id="lper-title">{t('lper.title')}</Text>
      <Text as="p" role="caption" tone="secondary">{t('lper.subtitle')}</Text>

      <Row gap={4} align="center" wrap>
        <SegmentedControl<PeriodPreset> ariaLabel={t('lper.period')} value={preset} onChange={setPreset} options={PERIOD_PRESETS.map((p) => ({ id: p, label: t(`lper.preset.${p}`) }))} />
        <DateRangeInput idPrefix="lper" from={shown.fromDay} to={shown.toDay} onFromChange={(d) => editDay('from', d)} onToChange={(d) => editDay('to', d)} fromLabel={t('lper.from')} toLabel={t('lper.to')} invalid={!valid} />
      </Row>

      {!valid && <Notice tone="warning">{t('lmap.err.period')}</Notice>}
      <Grid min="sm" gap={4}>
        {view === undefined
          ? error === undefined && CARD_IDS.map((id) => <StatCard key={id} label={t(`lper.kpi.${id}`)} value="" loading loadingLabel={t('ui.loading')} />)
          : cards.map((c) => {
              const spark = sparkFor(c.id);
              return (
                <StatCard
                  key={c.id}
                  label={t(`lper.kpi.${c.id}`)}
                  value={cardValue(c)}
                  {...(c.kind === 'unavailable' ? { hint: t('lper.unavailable.hint') } : {})}
                  {...(c.kind === 'count' ? { count: { value: c.value, format: (n) => nf.format(n) } } : c.kind === 'ratio' ? { count: { value: c.value, format: (n) => pf.format(n), decimals: 4 } } : {})}
                  {...(spark !== undefined ? { trend: spark } : {})}
                  {...deltaProp(c.id)}
                />
              );
            })}
      </Grid>

      {(view !== undefined || loadingCharts) && (
        <Stack gap={4}>
          <Panel>
            <SectionTitle level={3} size="sub">{t('lper.chart.days')}</SectionTitle>
            <ColumnChart
              title={t('lper.chart.days')}
              categories={trend.map((d) => dayLabel(d.day))}
              series={[
                { key: 'completed', label: t('lper.chart.completed'), values: trend.map((d) => d.completed) },
                { key: 'noDriver', label: t('lper.chart.noDriver'), values: trend.map((d) => d.noDriver), tone: 'danger' },
                { key: 'other', label: t('lper.chart.other'), values: trend.map((d) => d.other) },
              ]}
              totalLabel={t('lper.chart.total')}
              formatValue={(n) => nf.format(n)}
              loading={loadingCharts}
              loadingLabel={t('ui.loading')}
              emptyLabel={t('lper.chart.empty')}
              height={220}
            />
          </Panel>
          {hours !== undefined && (
            <Panel>
              <SectionTitle level={3} size="sub">{t('lper.chart.hours')}</SectionTitle>
              <ColumnChart
                title={t('lper.chart.hours')}
                categories={hours.categories}
                series={[
                  { key: 'completed', label: t('lper.chart.completed'), values: hours.completed },
                  { key: 'noDriver', label: t('lper.chart.noDriver'), values: hours.noDriver, tone: 'danger' },
                  { key: 'other', label: t('lper.chart.other'), values: hours.other },
                ]}
                totalLabel={t('lper.chart.total')}
                formatValue={(n) => nf.format(n)}
                loading={loadingCharts}
                loadingLabel={t('ui.loading')}
                emptyLabel={t('lper.chart.empty')}
                height={200}
              />
              <Text as="p" role="caption" tone="secondary">{t('lper.chart.hours.note')}</Text>
              <Text as="p" role="caption" tone="secondary">{t('now.overlap')}</Text>
            </Panel>
          )}
          <Grid min="md" gap={4}>
            <Panel>
              <SectionTitle level={3} size="sub">{t('lper.status.title')}</SectionTitle>
              <DonutChart
                title={t('lper.status.title')}
                slices={slices.map((sl) => ({ key: sl.id, label: t(`lper.status.${sl.id}`), value: sl.value, ...(sl.id === 'noDriver' ? { tone: 'danger' as const } : sl.id === 'cancelled' ? { tone: 'warning' as const } : {}) }))}
                formatValue={(n) => nf.format(n)}
                formatShare={(n) => pf.format(n)}
                center={
                  <>
                    <CountUp value={view?.rides.requested ?? 0} format={(n) => nf.format(n)} />
                    <Text role="caption" tone="secondary">{t('lper.chart.total')}</Text>
                  </>
                }
                loading={loadingCharts}
                loadingLabel={t('ui.loading')}
                emptyLabel={t('lper.chart.empty')}
                size={176}
              />
            </Panel>
            <Panel>
              <SectionTitle level={3} size="sub">{t('lper.rates.title')}</SectionTitle>
              {loadingCharts || view?.rates !== undefined ? (
                <Row gap={6} align="stretch" justify="center" wrap>
                  <div className="vello-admin-a-ring"><RingStat label={t('lper.ring.completion')} value={(view?.rates?.completionRate ?? 0) * 100} max={100} loading={loadingCharts} loadingLabel={t('ui.loading')} formatCenter={(n) => pf.format(n / 100)} formatValue={(n) => pf.format(n / 100)} size={128} /></div>
                  <div className="vello-admin-a-ring"><RingStat label={t('lper.ring.fill')} value={(view?.rates?.fillRate ?? 0) * 100} max={100} loading={loadingCharts} loadingLabel={t('ui.loading')} formatCenter={(n) => pf.format(n / 100)} formatValue={(n) => pf.format(n / 100)} size={128} /></div>
                </Row>
              ) : (
                <Text as="p" role="caption" tone="secondary">{t('lper.unavailable.hint')}</Text>
              )}
            </Panel>
          </Grid>
        </Stack>
      )}

      <ListTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.currency}
        loading={view === undefined && error === undefined}
        loadingLabel={t('ui.loading')}
        error={error}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('lper.revenue.empty')} />}
        minWidth={720}
      />
    </Stack>
  );
}
